/* eslint-disable react/no-deprecated */
import React, { PureComponent } from "react"
import PropTypes from "prop-types"
import Measure from "react-measure"
import { connect } from "react-redux"
import { WINDOW } from "constants/environment"
import { withRadium, DetectCompact, keyframes, gtmEvent } from "higher-order-components"
import { actionCreators, selectors } from "store/actions/environment"
import {
  actionCreators as searchActionCreators,
  selectors as searchSelectors
} from "store/actions/search"
import gtmHandler from "AtomicUtils/gtmHandler"
import { gtmSearchActionTypes, gtmGlobalNavActionTypes } from "store/gtm-events"
import { bindAll, noop, focusWithoutScroll, getPageName } from "utils"
import { Body } from "containers"
import ZoomZoom from "utils/zoom-zoom.js"
import { AnimatedHamburgerIcon, GlobalNavDropdown, GlobalNavOverlay, Hyperlink } from "components"
import logo from "./mazda-logo.svg" // @todo inline these SVGs in icons
import logoCompact from "./mazda-logo-compact.svg" // @todo inline these SVGs in icons
import SubNavLarge from "./sub-nav-large"
import SubNavCompact from "./sub-nav-compact"
import componentParser from "AtomicUtils/componentParser"
import {
  GlobalNavSearchFieldSlider,
  GlobalNavSearchResultsDrawer,
  GlobalNavSearchFieldCompact
} from "../global-nav-search"
/* Top Level Navigation Component. */

const subNavTranslate = ["+3em", "+40px"]

class GlobalNavContainer extends PureComponent {
  constructor(props) {
    super(props)

    this.state = {
      active: -1,
      mainNavItemCount: this.props.compact ? 1 : (this.props.forMyMazdaAccountsOnly ? 4 : 3),
      loaded: false,
      open: false,
      searchOpen: false,
      resultsOpen: false,
      mobileSearchFocus: false
    }

    bindAll(
      [
        "shouldComponentUpdate",
        "handleDesktopNav",
        "handleOnResize",
        "toggleActive",
        "makeActive",
        "toggle",
        "open",
        "close"
      ],
      this
    )
  }

  componentDidMount() {
    ZoomZoom()
    this.setState({
      loaded: true,
      mainNavItemCount: this.props.compact
        ? 1
        : (this.props.forMyMazdaAccountsOnly ? 4 : 3)
    })
  }

  componentWillReceiveProps(nextProps) {
    if (this.props.compact !== nextProps.compact) {
      if (this.state.open && this.props.compact) {
        // Close mobile subnav if open when exiting compact
        Body.slide(false)
        this.setState({ open: false, active: -1 }, () => Body.unlock())
      }
      this.props.setNavSearchQuery("")
      this.setState({
        searchOpen: false,
        resultsOpen: false
      })
    }
    if (nextProps.navSearchQuery.trim().length) {
      this.setState({ resultsOpen: true })
    } else {
      this.setState({ resultsOpen: false })
    }
    this.setState({
      mainNavItemCount: nextProps.compact ? 1 : (nextProps.forMyMazdaAccountsOnly ? 4 : 3)
    })
  }

  componentDidUpdate() {
    if (Body) {
      if (!this.props.compact && this.state.open) Body.slide(false)
      else if (this.state.open) Body.slide(subNavTranslate)
    }
  }

  /**
   * @param {object} event - click / keyDown event
   */
  toggle(event) {
    event.persist()

    if (event.type === "keydown" && event.key !== "Enter") return

    if (this.state.open) this.close()
    else this.open()

    if (!this.props.compact) {
      this.setState({ active: this.state.mainNavItemCount })
    } else {
      this.props.gtmEvent({
        gtmID: this.state.open
          ? gtmGlobalNavActionTypes.HAMBURGER_COLLAPSE
          : gtmGlobalNavActionTypes.HAMBURGER_EXPAND,
        gtmPayload: {}
      })
    }
  }

  /**
   * @param {number} index - Index to toggle active
   */
  toggleActive(index) {
    if (this.state.active === index) {
      this.setState({ active: -1 }, () => {
        if (!this.state.open) Body.unlock()
      })
    } else {
      this.makeActive(index)
    }
  }

  makeActive(index) {
    if (this.state.open && index < this.state.mainNavItemCount) {
      this.setState({ active: index, open: false }, () => Body.lock(true))
    } else {
      this.setState({ active: index }, () => Body.lock(true))
    }
  }

  open() {
    if (Body) {
      if (this.props.compact) Body.slide(subNavTranslate)
      else Body.slide(false)
      this.setState({ open: true, active: -1 }, () => Body.lock(true))
    }
  }

  close(event) {
    if (event && event.type === "keydown" && event.key !== "Escape") return
    this.props.setNavSearchQuery("")
    Body.slide(false)
    this.setState({ open: false, active: -1 }, () => Body.unlock())
  }

  handleOnSearch = (q) => {
    if (q.length) {
      this.props.gtmEvent({
        gtmID: gtmSearchActionTypes.SITE_SEARCH_BAR_SEARCHED,
        gtmPayload: { query: q }
      })
      WINDOW.location.href = `${this.props.searchPageUrl}?q=${this.props.navSearchQuery}`
    }
  }

  handleOnSearchClear = () => {
    this.props.setNavSearchQuery("")
    this.setState({
      searchOpen: false,
      resultsOpen: false
    })
  }

  handleOnSearchChange = ({ target: { value } }) => {
    this.props.setNavSearchQuery(value)
  }

  handleCompactSearchFocus = () => {
    this.setState({ mobileSearchFocus: true })
  }

  handleCompactSearchBlur = () => {
    this.setState({ mobileSearchFocus: false })
  }

  /**
   * @param {Array|NavDropdown|GlobalNavLink} components - Nested Components.
   * @returns {ReactElement} React Component
   */
  render() {
    const {
      compact,
      navSearchQuery,
      searchPageUrl,
      searchPlaceholderText,
      compactSearchPlaceholderText,
      setGlobalHeaderHeight,
      displaySearchBox,
      image,
      mobileImage,
    } = this.props
    const { mainNavItemCount } = this.state
    const mainNavComponents = this.props.components.slice(0, mainNavItemCount)
    const subNavProps = {
      navItems: this.props.components.slice(mainNavItemCount),
      activeItemIndex: this.state.active - mainNavItemCount,
      toggleActive: index => this.toggleActive(mainNavItemCount + index),
      close: this.close,
      offsetTop: this.props.globalHeaderHeight
    }
    const searchEnabled = displaySearchBox
    const compactSearch = searchEnabled && compact
    const desktopSearch = searchEnabled && !compact
    // let pageName = WINDOW.location.pathname
    // if (pageName === '/') pageName = "home"

    return (
      <div id="header" data-id="header" style={styles.navWrapper}>
        <Measure
          client
          bounds
          onResize={({ client: { height }, bounds: { top } }) => {
            setGlobalHeaderHeight(height)
          }}>
          {({ measureRef }) => (
            <nav
              ref={measureRef}
              style={[styles.base, this.state.loaded ? { opacity: 1 } : { opacity: 0 }]}
              data-test="global-nav">
              <Hyperlink
                style={styles.logoLink}
                withoutStyle
                gtm={{
                  category: "Header Nav",
                  action: "Nav - Clicked home badge"
                }}
                href={"/"} >
                <img style={styles.logo} src={compact ? mobileImage : image} alt={"Mazda"} />
              </Hyperlink>
              <div
                style={{
                  flex: "1 1 100%",
                  position: "relative",
                  display: "flex",
                  justifyContent: "flex-end"
                }}>
                <div>
                  {mainNavComponents.map((Component, index) => {
                    const active = this.state.active === index
                    if (Component.name === "PersonalisationBlockItem") {
                      if (
                        (compact && (Component.props.mobile || Component.props.tablet)) ||
                        (!compact && Component.props.desktop)
                      ) {
                        Component = Component.props.components[0]
                      } else {
                        return null
                      }
                    }
                    return (
                      <div key={index} style={{ display: "inline-block", margin: "0 1.2em" }}>
                        {componentParser([Component], {
                          active,
                          toggleActive: () => this.toggleActive(index),
                          close: this.close,
                          height: this.props.globalHeaderHeight
                        })}
                      </div>
                    )
                  })}
                  {!desktopSearch && <span style={styles.divider} />}
                  <div style={{ display: "inline-block", margin: "1.5em 1.8em" }}>
                    {compact ? (
                      <AnimatedHamburgerIcon onClick={this.toggle} open={this.state.open} />
                    ) : (
                        <GlobalNavDropdown
                          toggleActive={this.toggle}
                          gtm={{
                            category: "Header Nav",
                            action: "Nav - Clicked \"MORE\""
                          }}
                          close={() => {
                            gtmHandler({
                              pageCategory: "Header Nav",
                              pageAction: "Nav - Closed \"MORE\""
                            })
                            this.close()
                          }}
                          label="More"
                          active={this.state.open}
                          height={this.props.globalHeaderHeight}>
                          <GlobalNavOverlay>
                            <SubNavLarge
                              {
                                ...{
                                  ...subNavProps,
                                  navItems: subNavProps.navItems.map(item => {
                                    if (item.name === "PersonalisationBlockItem") {
                                      if (item.props.desktop) {
                                        return item.props.components[0]
                                      }
                                      return null
                                    }
                                    return item
                                  })
                                }
                              }
                              toggleActive={index => this.makeActive(mainNavItemCount + index)} />
                          </GlobalNavOverlay>
                        </GlobalNavDropdown>
                      )}
                  </div>
                  {desktopSearch && <span style={styles.divider} />}
                  {desktopSearch && (
                    <GlobalNavSearchFieldSlider
                      innerInputRef={(input) => {
                        this.searchInput = input
                      }}
                      value={navSearchQuery}
                      open={this.state.searchOpen}
                      placeholderText={searchPlaceholderText}
                      onSearch={this.handleOnSearch}
                      onChange={this.handleOnSearchChange}
                      onClear={this.handleOnSearchClear}
                      onSlideEnd={(e, open) => {
                        if (open) focusWithoutScroll(this.searchInput)
                      }}
                      onOpen={() => {
                        this.searchInput.blur()
                        this.setState({ searchOpen: !this.state.searchOpen })
                        this.props.gtmEvent({
                          gtmID: gtmSearchActionTypes.CLICK_SEARCH_ICON,
                          gtmPayload: { pageName: getPageName() }
                        })
                      }} />
                  )}
                </div>
              </div>
            </nav>
          )}
        </Measure>
        {!!this.state.open &&
          (compact && (
            <SubNavCompact
              hideNavItems={this.state.mobileSearchFocus}
              {
                ...{
                  ...subNavProps,
                  navItems: subNavProps.navItems.map(item => {
                    if (item.name === "PersonalisationBlockItem") {
                      if (item.props.mobile || item.props.tablet) {
                        return item.props.components[0]
                      }
                      return null
                    }
                    return item
                  })
                }
              }
            >
              {compactSearch && (
                <GlobalNavSearchFieldCompact
                  innerInputRef={(input) => {
                    this.searchInput = input
                  }}
                  height={this.props.globalHeaderHeight}
                  value={navSearchQuery}
                  placeholderText={compactSearchPlaceholderText}
                  onSearch={this.handleOnSearch}
                  onChange={this.handleOnSearchChange}
                  onClear={this.handleOnSearchClear}
                  onFocus={this.handleCompactSearchFocus}
                  onBlur={this.handleCompactSearchBlur} />
              )}
              {compactSearch && (
                <GlobalNavSearchResultsDrawer open={this.state.resultsOpen}>
                  {this.state.resultsOpen && (
                    <Hyperlink
                      gtm={{
                        category: "Site Search Bar",
                        action: `Site Search Bar - Searched "${navSearchQuery}"`
                      }}
                      href={`${searchPageUrl}?q=${navSearchQuery}`}>
                      View all results >
                    </Hyperlink>
                  )}
                </GlobalNavSearchResultsDrawer>
              )}
            </SubNavCompact>
          ))}
        {desktopSearch && (
          <GlobalNavSearchResultsDrawer open={this.state.resultsOpen}>
            <div
              style={{
                margin: "0 3em 0 10em"
              }}>
              {this.state.resultsOpen && (
                <Hyperlink
                  gtm={{
                    category: "Site Search Bar",
                    action: `Site Search Bar - Searched "${navSearchQuery}"`
                  }}
                  href={`${searchPageUrl}?q=${navSearchQuery}`}>
                  View all results for "{navSearchQuery}"
                </Hyperlink>
              )}
            </div>
          </GlobalNavSearchResultsDrawer>
        )}
      </div>
    )
  }
}

GlobalNavContainer.propTypes = {
  navSearchQuery: PropTypes.string,
  setNavSearchQuery: PropTypes.func,
  setGlobalHeaderHeight: PropTypes.func,
  globalHeaderHeight: PropTypes.number,
  searchPageUrl: PropTypes.string,
  searchEnabled: PropTypes.bool,
  displaySearchBox: PropTypes.bool,
  searchPlaceholderText: PropTypes.string,
  forMyMazdaAccountsOnly: PropTypes.bool,
  children: PropTypes.array,
  compactSearchPlaceholderText: PropTypes.string,
  gtmEvent: PropTypes.func,
  compact: PropTypes.bool
}

GlobalNavContainer.defaultProps = {
  setGlobalHeaderHeight: noop,
  globalHeaderHeight: 0,
  compact: true,
  forMyMazdaAccountsOnly: false,
  compactSearchPlaceholderText: "Search",
  searchPlaceholderText: "What are you looking for?",
  searchPageUrl: "/search"
}

const mapStateToProps = state => ({
  globalHeaderHeight: selectors.getGlobalHeaderHeight(state),
  navSearchQuery: searchSelectors.getNavSearchQuery(state)
})

const mapDispatchToProps = dispatch => ({
  setGlobalHeaderHeight: height => dispatch(actionCreators.updateGlobalHeaderHeight(height)),
  setNavSearchQuery: query => dispatch(searchActionCreators.updateNavSearchQuery(query))
})

/** Top Level Navigation Component. */
export default withRadium(
  DetectCompact({
    threshold: 900
  }),
  gtmEvent(),
  connect(
    mapStateToProps,
    mapDispatchToProps
  )
)(GlobalNavContainer)

const logoKeyframes = keyframes({
  from: {
    opacity: 0
  },
  to: {
    opacity: 1
  }
})

const breakpoint1 = "@media (min-width: 900px)"

const styles = {
  navWrapper: {
    width: "100%",
    display: "block",
    position: "relative",
    background: "#101010",
    zIndex: 3
  },
  base: {
    overflow: "hidden",
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between",
    transition: "transform .44s, opacity .25s",
    alignItems: "center",
    background: "#101010",
    color: "white",
    width: "100%"
  },
  divider: {
    width: "1px",
    display: "inline-block",
    background: "#999",
    height: "35px",
    opacity: ".2",
    verticalAlign: "middle"
  },
  logo: {
    height: "35px",
    width: "auto",
    opacity: 0,
    margin: "1.2em 1em 1em 1.5em",
    animation: "x .75s .1s forwards",
    animationName: logoKeyframes,
    [breakpoint1]: {
      height: "65px"
    }
  },
  logoLink: {
    outline: 0,
    maxWidth: '75px'
  }
}



// WEBPACK FOOTER //
// ./src/containers/global-nav/index.jsx